import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $accountListStore, $initialCurrenciesAvailableStore, $initialAccountsStore } from '@lib/store';
import { useTabs, useView, ViewEnum } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { AccountStatus, SortType } from '@shared/api';
import { AccountsGrid, AccountsTable } from './widgets';
import { Cards, PageContainer, List as Transactions, View } from '@widgets';
import { Button, Icon, Select, Tabs, Text } from '@shared/widgets';
import GridViewIcon from '@assets/icons/grid-view.svg';
import LineViewIcon from '@assets/icons/line-view.svg';

export const AccountsPage = () => {
  const accountDefaultListStore = useUnit($initialAccountsStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const accountsStore = useUnit($accountListStore.store);

  const [status, setStatus] = useState<AccountStatus | undefined>(AccountStatus.Active);
  const [order, setOrder] = useState<SortType>(SortType.Desc);
  const [currency, setCurrency] = useState<string | undefined>();

  const tabsState = useTabs();
  const viewState = useView({ page: 'accounts' });
  const { t } = useTranslation('accounts');

  useEffect(() => {
    if (!accountDefaultListStore.data.length) return;
    const hasActiveAccounts = accountDefaultListStore.data.some((account) => account.status === AccountStatus.Active);
    if (status === AccountStatus.Active && !hasActiveAccounts) setStatus(undefined);
  }, [accountDefaultListStore.data, status]);

  useEffect(() => {
    $accountListStore.events.getAccounts({ Status: status, SortType: order, Currency: currency });
    return () => $accountListStore.events.clear();
  }, [status, order, currency]);

  const orderOptions = useMemo(
    () => [
      { label: t('order.asc', { ns: 'common' }), value: SortType.Asc },
      { label: t('order.desc', { ns: 'common' }), value: SortType.Desc },
    ],
    [t],
  );
  const statusOptions = useMemo(() => {
    const statuses = [...new Set(accountDefaultListStore.data.map((account) => account.status))];
    return statuses.map((status) => ({ label: t(`statuses.account.${status}`, { ns: 'common' }), value: status }));
  }, [accountDefaultListStore.data, t]);
  const accountIds = useMemo(
    () => (accountDefaultListStore.data || []).filter((item) => item.type === tabsState.tab).map((item) => item.id),
    [tabsState.tab, accountDefaultListStore.data],
  );
  const cardAccountsIds = useMemo(
    () => accountsStore.data.filter((account) => account.type === tabsState.tab).map((account) => account.id),
    [accountsStore.data, tabsState.tab],
  );
  return (
    <PageContainer
      title={t('title')}
      header={
        <div className='flex items-center gap-2'>
          <Text tag='h1' weight={PaletteUtils.isVcfcPalette ? 'medium' : 'semibold'}>
            {t('header')}
          </Text>
          {!accountsStore.loading && (
            <div
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-2xl dark:bg-primary',
                PaletteUtils.isInsigniaPalette && 'bg-soft-black',
                PaletteUtils.isVcfcPalette && 'bg-primary',
              )}
            >
              <Text tag='span' size='2xl' color='white'>
                {accountsStore.data.length}
              </Text>
            </div>
          )}
        </div>
      }
      topRightAddons={
        <>
          <Button
            className={cn('h-10 w-10 rounded-sm', PaletteUtils.isVcfcPalette && 'bg-light/10', 'hover:bg-light/60 dark:hover:bg-light/30')}
            color='icon'
            onClick={() => viewState.onChangeView(ViewEnum.Grid)}
          >
            <Icon
              className={cn(
                PaletteUtils.isInsigniaPalette && [viewState.isGrid && '!text-primary'],
                PaletteUtils.isVcfcPalette && ['dark:!text-white/50', viewState.isGrid && '!text-primary dark:!text-white'],
              )}
              icon={<GridViewIcon />}
            />
          </Button>
          <Button
            className={cn('h-10 w-10 rounded-sm', PaletteUtils.isVcfcPalette && 'bg-light/10', 'hover:bg-light/60 dark:hover:bg-light/30')}
            color='icon'
            onClick={() => viewState.onChangeView(ViewEnum.List)}
          >
            <Icon
              className={cn(
                PaletteUtils.isInsigniaPalette && [viewState.isList && '!text-primary'],
                PaletteUtils.isVcfcPalette && ['dark:!text-white/50', viewState.isList && '!text-primary dark:!text-white'],
              )}
              icon={<LineViewIcon />}
            />
          </Button>
        </>
      }
    >
      <main className='flex flex-col gap-6'>
        {tabsState.hasCard && tabsState.hasPayment && <Tabs options={tabsState.tabs} value={tabsState.tab!} onChange={tabsState.onChangeTab} />}
        <section
          className={cn(
            'flex flex-col gap-6',
            PaletteUtils.isVcfcPalette &&
              viewState.isList &&
              'rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black',
          )}
        >
          <div className={cn('flex', 'max-[900px]:flex-col max-[900px]:gap-2')}>
            <div
              className={cn(
                'ml-auto flex flex-row space-x-4',
                'max-[900px]:ml-0',
                PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-2 max-md:space-x-0',
              )}
            >
              <Select
                className={cn('min-w-[140px]', PaletteUtils.isVcfcPalette && 'max-md:w-full')}
                items={statusOptions}
                nullValue={{ isEnable: true, text: t('placeholders.allStatuses', { ns: 'common' }) }}
                value={status}
                onChange={setStatus}
              />
              <Select className='min-w-[190px]' items={orderOptions} value={order} onChange={(val) => setOrder(val!)} />
              <Select
                className={cn('min-w-[160px]', PaletteUtils.isVcfcPalette && 'max-md:w-full')}
                items={currenciesAvailableStore.data}
                nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
                value={currency}
                onChange={(val) => setCurrency(val!)}
              />
            </div>
          </div>
          <View
            loading={accountsStore.loading}
            error={accountsStore.error}
            onRefresh={() => $accountListStore.events.getAccounts({ Status: status, SortType: order, Currency: currency })}
          >
            {viewState.isGrid && <AccountsGrid accountType={tabsState.tab!} accounts={accountsStore.data} currency={currency} />}
            {viewState.isList && <AccountsTable accountType={tabsState.tab!} accounts={accountsStore.data} />}
          </View>
        </section>
        <Cards
          className={cn(PaletteUtils.isVcfcPalette && 'rounded-xl bg-white dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black')}
          title={t('card.title')}
          listType={viewState.isList}
          accountIds={cardAccountsIds}
          currency={currency}
        />
        <Transactions accountType={tabsState.tab!} accountIds={accountIds} search filter />
      </main>
    </PageContainer>
  );
};
